import { z } from 'zod';
import { IsoDateTime, Pence, Uuid } from '../primitives.js';
import { LedgerDirection } from './vouchers.js';

/**
 * WS-13 Feature 3a — PREPAID PACKAGES / courses (LRU-14). A near-exact CLONE of the VOUCHERS double-entry
 * liability ledger (./vouchers.ts; ADR-0009 + ADR-0008).
 *
 * "Accounting is the point": SELLING a prepaid package (a course of sessions, or a stored value balance)
 * is a LIABILITY (package_liability), never revenue; revenue (+VAT) is recognised ONLY at REDEMPTION,
 * decrementing the liability — so a prepaid course can never count twice (the Slick double-count). A
 * client_package's REMAINING sessions and value, and the salon's OUTSTANDING package liability, are NEVER
 * stored as mutable numbers — they are always the signed sum of append-only ledger entries.
 *
 * VAT (ADR-0008): a multi-purpose package (`purpose='multi'`, the default) carries NO VAT at sale; VAT
 * lands at redemption on the real serviced lines. A single-purpose package (`purpose='single'`) is
 * DEFERRED (the same SPV deferral vouchers carries) — it needs a deferred-revenue contra-account design
 * with accountant sign-off, so the sell route rejects it with 422. RECURRING MEMBERSHIPS
 * (`kind='membership_recurring'`) are a SEPARATE later workflow (a connected-account Stripe subscription
 * plane): the kind is carried in the enum for forward-compatibility but is REJECTED at the sell route
 * with 422 for now (exactly as vouchers defers SPV).
 *
 * Money is integer pence; redeemable amounts are VAT-INCLUSIVE. The LedgerDirection enum is REUSED from
 * vouchers (debit/credit).
 */

// ── Enums ────────────────────────────────────────────────────────────────────

/**
 * The instrument type. `prepaid_sessions` = a course of N treatments (sessions are the liability);
 * `prepaid_value` = a stored monetary balance redeemed by value; `membership_recurring` = a £X/month
 * subscription (DEFERRED to a separate workflow — carried for forward-compatibility, rejected at sale).
 */
export const PackageKind = z.enum(['prepaid_sessions', 'prepaid_value', 'membership_recurring']);
export type PackageKind = z.infer<typeof PackageKind>;

/**
 * VAT class, frozen at sale (ADR-0008 §1, immutable per sold instance). `multi` = MPV (no VAT at sale,
 * VAT at redemption); `single` = SPV (DEFERRED — rejected at sale). Default `multi`.
 */
export const PackagePurpose = z.enum(['multi', 'single']);
export type PackagePurpose = z.infer<typeof PackagePurpose>;

export const PackageStatus = z.enum(['active', 'expired', 'void']);
export type PackageStatus = z.infer<typeof PackageStatus>;

/** The double-entry accounts a package ledger entry can post to (ADR-0009, mirrors VoucherAccount). */
export const PackageAccount = z.enum([
  'package_liability',
  'cash',
  'card',
  'revenue',
  'vat_output',
  'comp_expense',
  'breakage_income',
]);
export type PackageAccount = z.infer<typeof PackageAccount>;

/** The business event a balanced set of ledger rows records (groups them by `event_id`). */
export const PackageEventKind = z.enum(['sale', 'comp_issue', 'redemption', 'expiry', 'void']);
export type PackageEventKind = z.infer<typeof PackageEventKind>;

// ── Catalogue package (entity) ─────────────────────────────────────────────────

/** What one catalogue package grants (a service, a count of sessions, and/or a per-line discount). */
export const PackageItem = z
  .object({
    id: Uuid,
    salonId: Uuid,
    packageId: Uuid,
    serviceId: Uuid.nullable().describe('null = any service (a value package / generic course)'),
    sessionsGranted: z.int().min(1).nullable(),
    discountBps: z.int().min(0).max(10000).nullable(),
    createdAt: IsoDateTime,
  })
  .meta({ id: 'PackageItem' });
export type PackageItem = z.infer<typeof PackageItem>;

/**
 * A catalogue SKU a salon sells. `pricePence` is the list price; a SOLD instrument freezes its own
 * original_amount_pence so a later catalogue edit never re-prices a live sale. `purpose`/`kind` are frozen
 * once any instrument is sold against this package.
 */
export const Package = z
  .object({
    id: Uuid,
    salonId: Uuid,
    renterId: Uuid.nullable().describe(
      'the book that owns it; null for a shared/floor catalogue item',
    ),
    name: z.string(),
    kind: PackageKind,
    pricePence: Pence.describe('catalogue list price (VAT-inclusive); always > 0'),
    validityDays: z.int().min(1).nullable().describe('null = the instrument never expires'),
    purpose: PackagePurpose,
    active: z.boolean(),
    items: z.array(PackageItem),
    createdByStaffId: Uuid.nullable(),
    createdAt: IsoDateTime,
    updatedAt: IsoDateTime,
  })
  .meta({ id: 'Package' });
export type Package = z.infer<typeof Package>;

// ── Client package (the purchased instrument) ──────────────────────────────────

/** One append-only journal row. A correction is a NEW reversing event, never an edit. */
export const ClientPackageLedgerEntry = z
  .object({
    id: Uuid,
    salonId: Uuid,
    renterId: Uuid.nullable(),
    clientPackageId: Uuid,
    eventId: Uuid.describe('groups the balanced rows of one transaction'),
    eventKind: PackageEventKind,
    account: PackageAccount,
    direction: LedgerDirection,
    amountPence: Pence.describe('always > 0; the sign is carried by `direction`'),
    sessionsDelta: z
      .int()
      .nullable()
      .describe(
        'signed session movement for a prepaid_sessions package (the sign carries the meaning)',
      ),
    appointmentId: Uuid.nullable(),
    receiptId: Uuid.nullable(),
    reason: z.string().nullable().describe('actor-supplied reason for comp/void (audit trail)'),
    createdByStaffId: Uuid.nullable().describe('the acting staff member (no silent balance edits)'),
    createdAt: IsoDateTime,
  })
  .meta({ id: 'ClientPackageLedgerEntry' });
export type ClientPackageLedgerEntry = z.infer<typeof ClientPackageLedgerEntry>;

/**
 * The DERIVED balance of one purchased instrument — the signed sum of its ledger entries, NEVER stored.
 * `remainingValuePence` is the package_liability signed sum (credits − debits); `remainingSessions` is the
 * sessions_delta signed sum (sale credits +N, redemption debits −1 each), null for a value-only package.
 */
export const ClientPackageBalance = z
  .object({
    clientPackageId: Uuid,
    status: PackageStatus,
    kind: PackageKind,
    purpose: PackagePurpose,
    originalAmountPence: Pence,
    originalSessions: z.int().min(1).nullable(),
    /** DERIVED remaining value = signed sum of this instrument's package_liability ledger entries. */
    remainingValuePence: Pence,
    /** DERIVED remaining sessions = signed sum of sessions_delta; null for a value-only package. */
    remainingSessions: z.int().nullable(),
    expiresAt: IsoDateTime.nullable(),
  })
  .meta({ id: 'ClientPackageBalance' });
export type ClientPackageBalance = z.infer<typeof ClientPackageBalance>;

/**
 * One purchased instrument with its derived balance. The remaining sessions/value are DERIVED (the signed
 * sum of the ledger), never read from a column.
 */
export const ClientPackage = z
  .object({
    id: Uuid,
    salonId: Uuid,
    renterId: Uuid.nullable(),
    packageId: Uuid,
    clientId: Uuid,
    code: z.string().nullable(),
    kind: PackageKind,
    purpose: PackagePurpose,
    status: PackageStatus,
    name: z.string().describe('the catalogue package name at the time of view'),
    originalAmountPence: Pence,
    originalSessions: z.int().min(1).nullable(),
    /** DERIVED remaining value = signed sum of the instrument's package_liability ledger entries. */
    remainingValuePence: Pence,
    /** DERIVED remaining sessions = signed sum of sessions_delta; null for a value-only package. */
    remainingSessions: z.int().nullable(),
    purchasedAt: IsoDateTime,
    expiresAt: IsoDateTime.nullable(),
    createdByStaffId: Uuid.nullable(),
    createdAt: IsoDateTime,
  })
  .meta({ id: 'ClientPackage' });
export type ClientPackage = z.infer<typeof ClientPackage>;

/** A purchased instrument with its full append-only ledger + derived balance (staff/till view). */
export const ClientPackageWithLedger = ClientPackage.extend({
  ledger: z.array(ClientPackageLedgerEntry),
}).meta({ id: 'ClientPackageWithLedger' });
export type ClientPackageWithLedger = z.infer<typeof ClientPackageWithLedger>;

// ── Requests ─────────────────────────────────────────────────────────────────

/** Create a catalogue package + its items (manager). `purpose`/`kind` are frozen once any is sold. */
export const PackageItemInput = z.object({
  serviceId: Uuid.nullish().describe('null = any service'),
  sessionsGranted: z.int().min(1).nullish(),
  discountBps: z.int().min(0).max(10000).nullish(),
});
export type PackageItemInput = z.infer<typeof PackageItemInput>;

export const CreatePackage = z
  .object({
    name: z.string().trim().min(1).max(200),
    kind: PackageKind.default('prepaid_sessions'),
    pricePence: Pence.refine((v) => v > 0, 'price must be > 0'),
    validityDays: z.int().min(1).max(3650).nullish(),
    purpose: PackagePurpose.default('multi'),
    items: z.array(PackageItemInput).default([]),
  })
  .meta({ id: 'CreatePackage' });
export type CreatePackage = z.infer<typeof CreatePackage>;

/** Edit / deactivate a catalogue package. price/purpose/kind are immutable once any instrument is sold. */
export const UpdatePackage = z
  .object({
    name: z.string().trim().min(1).max(200).optional(),
    pricePence: Pence.refine((v) => v > 0, 'price must be > 0').optional(),
    validityDays: z.int().min(1).max(3650).nullish(),
    purpose: PackagePurpose.optional(),
    kind: PackageKind.optional(),
    active: z.boolean().optional(),
  })
  .meta({ id: 'UpdatePackage' });
export type UpdatePackage = z.infer<typeof UpdatePackage>;

/**
 * SELL a package to a client — issue the instrument + record the SALE as a double-entry event (credit
 * package_liability + debit cash/card for the price). Idempotent on `idempotencyKey`. `kind`
 * 'membership_recurring' is REJECTED with 422 (recurring memberships are a separate later workflow);
 * `purpose` 'single' is REJECTED with 422 (SPV deferral). sale_payment_id / sale_receipt_id link the
 * sale to the money it represents, if already recorded.
 */
export const SellPackage = z
  .object({
    packageId: Uuid,
    clientId: Uuid,
    /** SALE = money in (debit cash/card); COMP = gifted balance (debit comp_expense, no money in). */
    tender: z.enum(['cash', 'card', 'comp']).default('card'),
    /** Required for a comp (the audit reason); optional otherwise. */
    reason: z.string().trim().min(1).max(500).optional(),
    salePaymentId: Uuid.nullish(),
    saleReceiptId: Uuid.nullish(),
    idempotencyKey: z.string().min(8).max(200),
  })
  .meta({ id: 'SellPackage' });
export type SellPackage = z.infer<typeof SellPackage>;

/**
 * REDEEM N sessions / value against an OPTIONAL appointment. `redeemedLines` are the REAL services
 * tendered against (VAT-inclusive), so the VAT engine recognises output VAT on the actual supply (MPV).
 * `amountPence` is the value redeemed and must equal Σ redeemed line gross. `sessions` is how many
 * sessions to debit for a prepaid_sessions package (defaults to 1). Idempotent on `idempotencyKey`.
 */
export const RedeemPackageLine = z
  .object({
    serviceId: Uuid.nullish(),
    productId: Uuid.nullish(),
    description: z.string().min(1).max(200),
    qty: z.int().min(1).default(1),
    unitPricePence: Pence.describe('VAT-inclusive unit price of the redeemed line'),
    performedByStaffId: Uuid.nullish(),
  })
  .meta({ id: 'RedeemPackageLine' });
export type RedeemPackageLine = z.infer<typeof RedeemPackageLine>;

export const RedeemPackage = z
  .object({
    /** Total value to redeem (VAT-inclusive); must equal Σ redeemedLines gross and be ≤ remaining value. */
    amountPence: Pence.refine((v) => v > 0, 'amount must be > 0'),
    redeemedLines: z.array(RedeemPackageLine).min(1),
    /** How many sessions to debit for a prepaid_sessions package (must be ≤ remaining sessions). */
    sessions: z.int().min(1).default(1),
    appointmentId: Uuid.nullish().describe('the appointment this redemption serviced, if any'),
    idempotencyKey: z.string().min(8).max(200),
  })
  .meta({ id: 'RedeemPackage' });
export type RedeemPackage = z.infer<typeof RedeemPackage>;

// ── Reports ────────────────────────────────────────────────────────────────────

/**
 * The package liability reconciliation report (mirrors VoucherLiabilityReport). Takings read `revenue`
 * (+vat_output) from the redemption event, NEVER package_liability. INVARIANT: sold − redeemed − expired =
 * outstanding = −Σ(signed package_liability), to the penny.
 */
export const PackageLiabilityReport = z
  .object({
    soldPence: Pence.describe('Σ package_liability credits from SALE + COMP issue events'),
    redeemedPence: Pence.describe('Σ package_liability debits from REDEMPTION events'),
    expiredPence: Pence.describe('Σ package_liability debits from EXPIRY/VOID events'),
    outstandingPence: Pence.describe(
      'the live liability = Σ(signed package_liability) = credits − debits',
    ),
    revenueRecognisedPence: Pence,
    vatOutputPence: Pence,
    reconciles: z.boolean().describe('sold − redeemed − expired === outstanding (must be true)'),
    activePackageCount: z.int().min(0),
    generatedAt: IsoDateTime,
  })
  .meta({ id: 'PackageLiabilityReport' });
export type PackageLiabilityReport = z.infer<typeof PackageLiabilityReport>;

// ── Query schemas (NO .meta — entity-only carry .meta({ id })) ─────────────────

export const ListClientPackagesQuery = z.object({
  clientId: Uuid.optional(),
  status: PackageStatus.optional(),
});
export type ListClientPackagesQuery = z.infer<typeof ListClientPackagesQuery>;

// ── Client portal DTO (NO internal ids — customer-facing only) ─────────────────

/**
 * The client-portal "Packages" view for ONE purchased instrument. Customer-facing ONLY: NO clientId /
 * packageId / renterId / ledger ids — the package name + derived remaining sessions/value + expiry.
 */
export const ClientPackageView = z
  .object({
    salonName: z.string(),
    name: z.string(),
    kind: PackageKind,
    status: PackageStatus,
    originalAmountPence: Pence,
    originalSessions: z.int().min(1).nullable(),
    /** DERIVED remaining value (signed sum of the ledger), never stored. */
    remainingValuePence: Pence,
    /** DERIVED remaining sessions (signed sum of sessions_delta); null for a value-only package. */
    remainingSessions: z.int().nullable(),
    purchasedAt: IsoDateTime,
    expiresAt: IsoDateTime.nullable(),
  })
  .meta({ id: 'ClientPackageView' });
export type ClientPackageView = z.infer<typeof ClientPackageView>;
